import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Modal from 'reactstrap/lib/Modal';
import Input from 'reactstrap/lib/Input';
import { connect } from 'react-redux';
import { kebabCase, without, uniq } from 'lodash-es';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { shuffle } from 'client/utils/seed-randomizers';
import { Storage } from 'site-modules/shared/utils/storage';
import { CANVAS_CREATIVE_ID, CANVAS_ICONS } from 'site-modules/canvas/constants/canvas';

import './chat-survey.scss';

const THUMB_VALUE = {
  UP: 'approval',
  DOWN: 'disapproval',
};
const THUMB_BUTTONS = [
  {
    icon: CANVAS_ICONS.THUMB_UP,
    hoverIcon: CANVAS_ICONS.THUMB_UP_SOLID,
    label: 'Good',
    value: THUMB_VALUE.UP,
  },
  {
    icon: CANVAS_ICONS.THUMB_DOWN,
    hoverIcon: CANVAS_ICONS.THUMB_DOWN_SOLID,
    label: 'Need improvements',
    value: THUMB_VALUE.DOWN,
  },
];
const MODAL_SURVEY_MAP = {
  [THUMB_VALUE.UP]: {
    heading: 'Glad to hear it!',
    subheading: 'What is making this experience positive for you so far?',
    options: [
      'Good information',
      'Fast and efficient',
      'Follows instructions',
      'Helpful for planning',
      'Provides useful next steps',
    ],
  },
  [THUMB_VALUE.DOWN]: {
    heading: 'Sorry about that. How can we improve?',
    subheading: 'Your feedback matters and helps shape our product.',
    options: [
      'Difficult to understand',
      'Missing key information',
      'Doesn’t follow instructions',
      'Not helpful for planning',
      'Doesn’t provide useful next steps',
    ],
  },
};
const OPTION_NAME = 'chat-survey';
const CREATIVE_ID = 'chat-canvas-user-survey';
const SURVEY_STORAGE_KEY = 'chat-survey';
const TWO_MINUTES = 120000;

export function ChatSurveyUI({ className = null, disableCanvasSurvey = false }) {
  const [showSurvey, setShowSurvey] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isThankYouModal, setThankYouModal] = useState(false);
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);
  const [selectedValue, setSelectedValue] = useState(null);
  const trackingValues = useRef({ options: [], text: '' });
  const localStorage = useRef();
  const [setShowTimeout] = useTimeout();
  const [setCloseTimeout] = useTimeout();

  useEffect(() => {
    localStorage.current = new Storage('localStorage');

    if (disableCanvasSurvey || localStorage.current.get(SURVEY_STORAGE_KEY)) {
      setShowSurvey(null);
      return;
    }

    setShowTimeout(() => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'show_user_survey',
          creative_id: CANVAS_CREATIVE_ID,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        },
      });
      setShowSurvey(true);
    }, TWO_MINUTES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalSurveyData = useMemo(() => {
    if (isThankYouModal) {
      return {
        heading: 'Thank you!',
        subheading: 'We appreciate your feedback, it helps us improve! 🙂',
      };
    }

    return (
      selectedValue && {
        ...MODAL_SURVEY_MAP[selectedValue],
        options: [...shuffle(MODAL_SURVEY_MAP[selectedValue].options), 'Other'],
      }
    );
  }, [isThankYouModal, selectedValue]);

  const onThumbClick = useCallback(
    ({
      currentTarget: {
        dataset: { value },
      },
    }) => {
      setSelectedValue(value);
      setModalOpen(true);
      setShowSurvey(false);
      localStorage.current.set(SURVEY_STORAGE_KEY, '1');
    },
    []
  );

  const checkSubmitDisabled = useCallback(() => {
    setSubmitDisabled(!trackingValues.current.text.length && !trackingValues.current.options.length);
  }, []);

  const onCheckboxChange = useCallback(
    ({ currentTarget: { checked, value } }) => {
      trackingValues.current.options = checked
        ? uniq([...trackingValues.current.options, value])
        : without(trackingValues.current.options, value);
      checkSubmitDisabled();
    },
    [checkSubmitDisabled]
  );

  const onInputChange = useCallback(
    ({ currentTarget: { value } }) => {
      trackingValues.current.text = value.trim();
      checkSubmitDisabled();
    },
    [checkSubmitDisabled]
  );

  const showThankYouModal = useCallback(() => {
    setThankYouModal(true);
    setModalOpen(true);
    setCloseTimeout(() => {
      setModalOpen(false);
    }, 2500);
  }, [setCloseTimeout]);

  const closeModal = useCallback(() => {
    if (!isThankYouModal) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ENGAGE_SURVEY,
          subaction_name: `user_${selectedValue}_survey`,
          creative_id: CREATIVE_ID,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          value: trackingValues.current.options.join(),
        },
      });
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ENGAGE_SURVEY,
          subaction_name: `user_${selectedValue}_survey`,
          creative_id: CREATIVE_ID,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          value: `text=${trackingValues.current.text}`,
        },
      });
    }

    setModalOpen(false);
  }, [isThankYouModal, selectedValue]);

  if (disableCanvasSurvey || showSurvey === null) {
    return null;
  }

  return (
    <Fragment>
      <div className={classnames('chat-survey w-100', className, { 'mb-0_75 pt-0_75 show': showSurvey })}>
        <div className="d-flex justify-content-between align-items-center w-100 bg-cool-gray-80 rounded-16 small py-0_25 px-1">
          How are we doing so far?
          <div className="d-flex gap-0_5">
            {THUMB_BUTTONS.map(({ icon, hoverIcon, label, value }) => (
              <Button
                key={label}
                className="thumb-button p-0 rounded pos-r"
                onClick={onThumbClick}
                color="link"
                data-value={value}
                data-tracking-id={`user_${value}_submit`}
                data-tracking-ignore-value
              >
                <span className="visually-hidden">{label}</span>
                <img className="icon" src={icon} alt="" width={18} />
                <img className="icon hover pos-a top-0 left-0" src={hoverIcon} alt="" width={18} />
              </Button>
            ))}
          </div>
        </div>
      </div>
      <Modal
        className="chat-survey-modal my-0 mx-auto"
        contentClassName="rounded-16 py-2 px-1_5"
        centered
        isOpen={isModalOpen}
        onClosed={isThankYouModal ? undefined : showThankYouModal}
        toggle={closeModal}
      >
        {!!modalSurveyData && (
          <Fragment>
            <div className="heading-3 mb-0_5">{modalSurveyData.heading}</div>
            <div className="text-cool-gray-10 fw-medium">{modalSurveyData.subheading}</div>
            {modalSurveyData.options?.length && (
              <Fragment>
                <fieldset className="mt-1_5">
                  <legend className="text-cool-gray-10 small mb-1">Choose as many as you like:</legend>
                  <ul className="options-list list-unstyled mb-1_5">
                    {modalSurveyData.options.map(option => (
                      <li className="mb-0_75" key={option}>
                        <input
                          onChange={onCheckboxChange}
                          type="checkbox"
                          id={kebabCase(option)}
                          name={OPTION_NAME}
                          value={option}
                        />
                        <label htmlFor={kebabCase(option)} className="fw-medium rounded-16 px-0_75 py-0_25 mb-0">
                          <img src={CANVAS_ICONS.CHECK} className="option-icon me-0_5" alt="" />
                          {option}
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
                <fieldset className="mb-1_5">
                  <legend className="small mb-0_5">Please tell us more (Optional)</legend>
                  <Input
                    onChange={onInputChange}
                    placeholder="Type your feedback here..."
                    className="feedback-input py-0_75 size-16"
                    maxLength={300}
                  />
                </fieldset>
                <div className="d-flex gap-1_5">
                  <Button
                    size="lg"
                    color="blue-50"
                    className="text-transform-none fw-medium rounded-8 px-1 py-0_5"
                    onClick={closeModal}
                    disabled={isSubmitDisabled}
                  >
                    Send Feedback
                  </Button>
                  <Button
                    size="lg"
                    color="outline-info"
                    className="text-transform-none fw-medium rounded-8 px-1 py-0_5"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                </div>
                <Button color="link" size="lg" className="pos-a top-0 right-0 p-0_5 rounded-16" onClick={closeModal}>
                  <span className="visually-hidden">Close survey</span>
                  <i className="icon-cross3 text-cool-gray-10 size-24" aria-hidden />
                </Button>
              </Fragment>
            )}
          </Fragment>
        )}
      </Modal>
    </Fragment>
  );
}

ChatSurveyUI.propTypes = {
  className: PropTypes.string,
  disableCanvasSurvey: PropTypes.bool,
};

const mapStateToProps = state => ({
  disableCanvasSurvey: !!state.featureFlags?.disableCanvasSurvey,
});

export const ChatSurvey = connect(mapStateToProps)(ChatSurveyUI);
